import { httpGet, httpPost } from '../../modules/api/axios_api';
import { theme } from '../theme';
import Constants, { fixProtocol } from '../../constants/constants';

export const PARAMS = {
  DATE_FROM: 'last_activity_date_from',
  DATE_TO: 'last_activity_date_to',
  SORT_BY: 'sort',
  SORT_DIRECTION: 'sort_desc',
  NAME: 'patient_name',
  PAGE: 'offset',
  PER_PAGE: 'limit',
  PLAYERS_TO_EXCLUDE: 'players_to_exclude',
};

export const getInitials = (name) => {
  const splitName = name.split(' ');
  const firstLetter = splitName[0][0];
  const lastLetter = splitName.length > 1 ? splitName[splitName.length - 1][0] : '';

  return `${firstLetter}${lastLetter}`;
};

export const getAvatarColor = (initials) => {
  const { color } = theme;

  const possibleColors = [
    color.primary.regular,
    color.secondary.regular,
    color.tertiary.regular,
    color.primary.dark,
    color.secondary.dark,
    color.tertiary.dark,
    color.accentPrimary.regular,
    color.accentSecondary.regular,
    color.accentTertiary.regular,
  ];

  // Get steps of range
  const steps = 51 / possibleColors.length;

  let value = 0;

  for (let i = 0; i < initials.length; i++) {
    // Get character value between 0 and 25 (a - z)
    const charValue = initials.toLowerCase().charCodeAt(i) - 97;

    // Add to total value if within proper range
    value = value + charValue >= 0 && charValue <= 51 ? charValue : 0;
  }

  // Test value to see what color it should associate with
  for (let i = 0; i < possibleColors.length; i++) {
    if (value >= i * steps && value <= (i + 1) * steps) {
      return possibleColors[i];
    }
  }
};

export const getPlayerCard = (id, session) => {
  const url = new URL(`${fixProtocol(Constants.BACKEND_URL)}/game_sessions/player_card/${id}`);
  const params = new URLSearchParams(url.search);

  params.append('my_session_only', typeof session !== 'undefined' ? session : 1);

  return httpGet(`${url.href}?${params.toString()}`, {})
    .then((results) => {
      if (results.data.success) {
        const mappedResults = {
          ...results.data.result,
        };

        if (results.data.result.lastSession.success !== false) {
          const assistance = results.data.result.lastSession.assistance;

          const mappedAssistance = assistance.map((measurement) => {
            const baseData = {
              name: measurement.name,
              value: measurement.value,
              type: 'range',
              showBackfill: true,
              showSteps: false,
              showValue: true,
              showNotApplicable: true,
            };

            let data = {};

            switch (measurement.name) {
              case 'cognitive_assistance':
                data = {
                  ...baseData,
                  label: 'Level of COGNITIVE assistance',
                };
                break;
              case 'physical_assistance':
                data = {
                  ...baseData,
                  label: 'Level of PHYSICAL assistance',
                };
                break;
              case 'standing_tolerance':
                data = {
                  ...baseData,
                  label: 'Standing tolerance',
                  note: '(how long the patient was standing)',
                };
                break;
              case 'resistance':
                data = {
                  ...baseData,
                  label: 'Applied Resistance',
                  showBackfill: false,
                  showValue: false,
                  showSteps: ['None', 'Light', 'Moderate', 'Heavy'],
                };
                break;
              case 'assistive_device':
                data = {
                  ...baseData,
                  label: 'Assistive Device',
                  type: 'radio',
                  options: [
                    {
                      value: 'none',
                      display: 'None',
                    },
                    {
                      value: 'r_walker',
                      display: 'R. Walker',
                    },
                    {
                      value: 's_walker',
                      display: 'S. Walker',
                    },
                    {
                      value: 'cane',
                      display: 'Cane',
                    },
                    {
                      value: 'other',
                      display: 'Other',
                    },
                  ],
                };
                break;
              default:
                break;
            }
            return data;
          });

          mappedResults.lastSession.assistance = mappedAssistance;
        }

        return mappedResults;
      }

      return;
    })
    .catch((error) => {
      throw new Error(`Error getting patient card: ${error}`);
    });
};

export const getPlayerList = async (parameters = null) => {
  const url = new URL(`${fixProtocol(Constants.BACKEND_URL)}/players/my-players`);
  const params = new URLSearchParams(url.search);

  params.append(PARAMS.PER_PAGE, parameters[PARAMS.PER_PAGE] || 10);
  parameters[PARAMS.DATE_FROM] && params.append(PARAMS.DATE_FROM, parameters[PARAMS.DATE_FROM]);
  parameters[PARAMS.DATE_TO] && params.append(PARAMS.DATE_TO, parameters[PARAMS.DATE_TO]);
  parameters[PARAMS.SORT_BY] && params.append(PARAMS.SORT_BY, parameters[PARAMS.SORT_BY]);
  parameters[PARAMS.SORT_DIRECTION] && params.append(PARAMS.SORT_DIRECTION, parameters[PARAMS.SORT_DIRECTION]);
  parameters[PARAMS.NAME] && params.append(PARAMS.NAME, parameters[PARAMS.NAME]);
  parameters[PARAMS.PAGE] && params.append(PARAMS.PAGE, parameters[PARAMS.PAGE] * parameters[PARAMS.PER_PAGE]);

  return httpGet(`${url.href}?${params.toString()}`, {})
    .then((results) => {
      if (results.data.success) {
        return results.data.result;
      }

      return;
    })
    .catch((error) => {
      throw new Error('Error getting patient list', error);
    });
};

export const addPlayer = async (firstName, lastName, shouldAddHomePlayer, login, password) => {
  const url = new URL(`${fixProtocol(Constants.BACKEND_URL)}/add-player`);

  const config = {
    first_name: firstName,
    last_name: lastName,
    should_create_home_player: shouldAddHomePlayer,
    login: login,
    password: password,
  };

  return httpPost(url, config);
};
